import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Form,
  FloatingLabel,
  OverlayTrigger,
  Popover,
  Button
} from 'react-bootstrap';
import {
  fetchHVACampaignNames,
  fetchHVAServiceNames,
  HVACampaignName,
  HVAServiceName
} from 'services/hvaservicecampaign.service';
import { getAllHVAAgents, HVAAgent } from 'services/hvaagent.service';
import DatePicker from 'components/base/DatePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsDownToLine } from '@fortawesome/free-solid-svg-icons';
import QAScorecardData1 from './QADashboardScorecardData1';

interface QAScorecardProps {
  scorecardid: number;
}

const QAScorecard: React.FC<QAScorecardProps> = ({ scorecardid }) => {
  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>([]);
  const [dateRangeOption, setDateRangeOption] = useState('last3months');
  const [campaigns, setCampaigns] = useState<HVACampaignName[]>([]);
  const [services, setServices] = useState<HVAServiceName[]>([]);
  const [hvaagents, setHVAAgents] = useState<HVAAgent[]>([]);
  const [formData, setFormData] = useState({
    service: '0',
    campaign: '0',
    hva: '0'
  });
  const [appliedFilters, setAppliedFilters] = useState({
    startdate: new Date(),
    enddate: new Date(),
    serviceid: 0,
    campaignid: 0,
    agentid: 0
  });
  const [showPopover, setShowPopover] = useState(false);
  const [shouldBounce, setShouldBounce] = useState(false);

  const getDefaultDateRange = () => {
    const today = new Date();
    const startDate = new Date(
      today.getFullYear(),
      today.getMonth() - 3,
      today.getDate()
    );
    setDateRangeOption('last3months');
    return [startDate, today];
  };

  const formatDateRange = (dates: Date[]) => {
    if (dates.length === 2) {
      return `${dates[0].toLocaleDateString()} - ${dates[1].toLocaleDateString()}`;
    }
    return '';
  };

  useEffect(() => {
    const defaultRange = getDefaultDateRange();
    setSelectedDateRange(defaultRange);
    setFormData({
      service: '0',
      campaign: '0',
      hva: '0'
    });
    setAppliedFilters({
      startdate: defaultRange[0],
      enddate: defaultRange[1],
      serviceid: 0,
      campaignid: 0,
      agentid: 0
    });
  }, [scorecardid]);

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const campaignNames = await fetchHVACampaignNames();
        setCampaigns(campaignNames);
      } catch (error) {
        alert('Error loading campaigns:');
      }
    };

    const loadServices = async () => {
      try {
        const serviceNames = await fetchHVAServiceNames();
        setServices(serviceNames);
      } catch (error) {
        alert('Error loading services:');
      }
    };

    const loadHVAAgents = async () => {
      try {
        const hvaAgents = await getAllHVAAgents();
        setHVAAgents(hvaAgents);
      } catch (error) {
        alert('Error loading agents:');
      }
    };

    loadCampaigns();
    loadServices();
    loadHVAAgents();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [id]: value
    }));
    setShouldBounce(true);
  };

  const handleDateChange = (date: Date[]) => {
    if (date.length === 2) {
      setSelectedDateRange(date);
      setShowPopover(false);
      setShouldBounce(true);
    }
  };

  const applyFilters = () => {
    setAppliedFilters({
      startdate: selectedDateRange[0],
      enddate: selectedDateRange[1],
      serviceid: Number(formData.service),
      campaignid: Number(formData.campaign),
      agentid: Number(formData.hva)
    });
    setShouldBounce(false);
  };

  const handleDateRangeOptionChange = (value: string) => {
    setDateRangeOption(value);
    let startDate: Date, endDate: Date;
    const today = new Date();

    switch (value) {
      case 'last30':
        startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = today;
        break;
      case 'last3months':
        startDate = new Date(
          today.getFullYear(),
          today.getMonth() - 3,
          today.getDate()
        );
        endDate = today;
        break;
      case 'thisyear':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = today;
        break;
      default:
        return; // Para 'custom', no cerramos el popover
    }

    setSelectedDateRange([startDate, endDate]);
    // Close popover if not custom range
    setShowPopover(false);
    setShouldBounce(true);
  };

  const datePopover = (
    <Popover id="date-range-popover" className="shadow-lg lh-sm fs-9">
      <Popover.Body>
        <p>Select a date range option:</p>
        <div className="mb-3">
          {['last30', 'last3months', 'thisyear', 'custom'].map(option => (
            <div key={option} className="form-check form-switch mb-0">
              <label className="d-flex fs-9 justify-content-start align-items-center mx-2">
                <input
                  type="checkbox"
                  value={option}
                  checked={dateRangeOption === option}
                  onChange={() => handleDateRangeOptionChange(option)}
                  className="form-check-input pt-0"
                />
                <div className="fs-9 fw-semibold pt-1 px-2">
                  {option === 'last30' && 'Last 30 days'}
                  {option === 'last3months' && 'Last 3 months'}
                  {option === 'thisyear' && 'This year'}
                  {option === 'custom' && 'Custom range'}
                </div>
              </label>
            </div>
          ))}
        </div>
        {dateRangeOption === 'custom' && (
          <>
            <p>Select a custom date range from the date picker below:</p>
            <DatePicker
              value={selectedDateRange}
              onChange={handleDateChange}
              options={{
                dateFormat: 'M j, Y',
                mode: 'range',
                onClose: () => setShowPopover(false)
              }}
            />
          </>
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <Row className="mb-1 mx-0 p-0 justify-content-start align-items-center">
        <div className="text-start px-0 pt-2">
          <label className="form-label px-2 text-primary">
            Scorecard filters {' (' + scorecardid + ')'}
          </label>
        </div>
        <div className="d-flex flex-wrap pb-2">
          <Row>
            <Col className="p-0 me-1">
              <FloatingLabel controlId="dateRange" label="Date Range">
                <OverlayTrigger
                  trigger="click"
                  placement="bottom-start"
                  overlay={datePopover}
                  rootClose={false}
                  show={showPopover} // Control popover visibility with state
                  onToggle={nextShow => setShowPopover(nextShow)} // Update showPopover when clicked
                >
                  <Form.Control
                    type="text"
                    value={formatDateRange(selectedDateRange)}
                    readOnly
                    style={{ width: '185px' }}
                  />
                </OverlayTrigger>
              </FloatingLabel>
            </Col>
            <Col className="p-0 me-1">
              <FloatingLabel controlId="service" label="Service">
                <Form.Select
                  id="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  style={{ width: '180px' }}
                >
                  <option value="0">Any service</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id.toString()}>
                      {service.name}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col className="p-0 me-1">
              <FloatingLabel controlId="campaign" label="Campaign">
                <Form.Select
                  id="campaign"
                  value={formData.campaign}
                  onChange={handleInputChange}
                  style={{ width: '220px' }}
                >
                  <option value="0">Any campaign</option>
                  {campaigns.map(campaign => (
                    <option key={campaign.id} value={campaign.id.toString()}>
                      {campaign.name}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col className="p-0 me-1">
              <FloatingLabel controlId="hva" label="Agent">
                <Form.Select
                  id="hva"
                  value={formData.hva}
                  onChange={handleInputChange}
                  style={{ width: '130px' }}
                >
                  <option value="0">Any agent</option>
                  {hvaagents.map(hva => (
                    <option key={hva.id} value={hva.id.toString()}>
                      {hva.name}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col className="d-flex justify-content-start align-items-center">
              <Button
                variant="link"
                onClick={applyFilters}
                className="px-1 pt-4"
              >
                <FontAwesomeIcon
                  icon={faArrowsDownToLine}
                  size="xl"
                  bounce={shouldBounce}
                />
              </Button>
            </Col>
          </Row>
        </div>

        <QAScorecardData1
          scorecardid={scorecardid}
          startdate={appliedFilters.startdate}
          enddate={appliedFilters.enddate}
          serviceid={appliedFilters.serviceid}
          campaignid={appliedFilters.campaignid}
          agentid={appliedFilters.agentid}
        />
      </Row>
    </>
  );
};

export default QAScorecard;
